import type { Paper } from '@realmery/realm-core';
import { useSyncExternalStore } from 'react';
import { AUTOMATIC, BUILT_IN_SKINS, applySkin, parseSkin, type Skin } from './skins.ts';

/**
 * Settings on this device (docs/PARITY-STEP-1-PLAN.md section 5, decision 2a), with the
 * desktop's keys, defaults and choices (`settings.Settings`) for what applies on the web.
 * They live in browser storage; every read and write is guarded, so storage that's
 * blocked, full or cleared just means the defaults.
 */

export const START_LAST_REALM = 'last-realm';
export const START_REALMS = 'welcome';
export const START_CHOSEN_REALM = 'chosen-realm';

export interface Settings {
  /** `general.onStart`. The web's realms list stands in for the desktop's welcome screen, and is the web's default. */
  readonly 'general.onStart': string;
  /** `general.startRealm`: here, the realm's key, not a file path. */
  readonly 'general.startRealm': string;
  readonly 'general.confirmDeletes': boolean;
  /** `AUTOMATIC`, a built-in skin's id, or `custom:<id>`. */
  readonly 'appearance.skin': string;
  readonly 'appearance.textSize': string;
  readonly 'appearance.writingFont': string;
  /** In points; 0 keeps the usual size. */
  readonly 'appearance.writingSize': number;
  readonly 'appearance.animations': boolean;
  readonly 'new.entryType': string;
  readonly 'new.entryAudience': string;
  /** How many rolls the Dice Roller's history keeps (10 to 5,000, in tens). */
  readonly 'dice.historyLimit': number;
  /** How many dice the Dice Roller's die buttons start on (1 to 100). */
  readonly 'new.diceCount': number;
  /** The word goal a new Story Studio scene starts with (0 to 100,000; 0 for none). */
  readonly 'new.sceneWordGoal': number;
  /** How many saved versions of each scene Restore can go back to (1 to 200). */
  readonly 'writing.sceneRevisions': number;
  /** Minutes without typing before focus mode's timer stops counting (1 to 60). */
  readonly 'writing.focusIdleMinutes': number;
  /** The paper character sheets print on, as the desktop's `print.paperSize`. */
  readonly 'print.paperSize': Paper;
  /** `atlas.units`: distances and areas in each map's own unit, or converted. */
  readonly 'atlas.units': 'map' | 'metric' | 'imperial';
  /** `new.mapGrid`: the grid a newly imported map starts with. */
  readonly 'new.mapGrid': 'NONE' | 'SQUARE' | 'HEX';
  /** `new.mapUnit`: the unit a new map's scale is measured in; empty follows Measurement units. */
  readonly 'new.mapUnit': string;
  /** The desktop's `proofing.enabled`: misspellings underlined as you write. */
  readonly 'writing.spelling': boolean;
  /** The desktop's `proofing.dialect`: the spellings accepted. */
  readonly 'writing.dialect': 'US' | 'UK';
}

export const DEFAULTS: Settings = {
  'general.onStart': START_REALMS,
  'general.startRealm': '',
  'general.confirmDeletes': true,
  'appearance.skin': AUTOMATIC,
  'appearance.textSize': '1.0',
  'appearance.writingFont': '',
  'appearance.writingSize': 0,
  'appearance.animations': true,
  'new.entryType': 'PERSON',
  'new.entryAudience': 'READER',
  'dice.historyLimit': 200,
  'new.diceCount': 1,
  'new.sceneWordGoal': 0,
  'writing.sceneRevisions': 20,
  'writing.focusIdleMinutes': 3,
  'print.paperSize': 'A4',
  'atlas.units': 'map',
  'new.mapGrid': 'NONE',
  'new.mapUnit': '',
  'writing.spelling': true,
  'writing.dialect': 'US',
};

export const UNIT_SYSTEMS = [
  ['map', "As each map's scale says"],
  ['metric', 'Metric (km, m)'],
  ['imperial', 'Imperial (miles, feet)'],
] as const;
export const MAP_GRIDS = [
  ['NONE', 'No grid'],
  ['SQUARE', 'Square grid'],
  ['HEX', 'Hex grid'],
] as const;

export const ON_START_CHOICES = [
  [START_REALMS, 'Show your realms'],
  [START_LAST_REALM, 'Reopen the last realm'],
  [START_CHOSEN_REALM, 'Open the realm chosen below'],
] as const;

export const TEXT_SIZES = [
  ['0.9', 'Smaller'],
  ['1.0', 'Normal'],
  ['1.15', 'Larger'],
  ['1.3', 'Largest'],
] as const;

/** The desktop picks from installed fonts; a browser can only ask for these by name, falling back when one's missing. */
export const WRITING_FONTS = [
  ['', 'The usual font'],
  ["Georgia, 'Times New Roman', serif", 'Georgia'],
  ["'Palatino Linotype', Palatino, 'Book Antiqua', serif", 'Palatino'],
  ["Garamond, 'EB Garamond', serif", 'Garamond'],
  ["'Times New Roman', Times, serif", 'Times New Roman'],
  ["system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif", 'The system font'],
  ['Verdana, Geneva, sans-serif', 'Verdana'],
  ["Consolas, 'Courier New', monospace", 'Monospace'],
] as const;

export const ENTRY_TYPES = ['PERSON', 'PLACE', 'FACTION', 'SPECIES', 'CREATURE', 'ITEM', 'EVENT', 'CONCEPT', 'CULTURE', 'TECHNOLOGY', 'RELIGION'] as const;
export const AUDIENCES = ['OWNER', 'EDITOR', 'CONTRIBUTOR', 'PLAYER', 'READER'] as const;

const SETTINGS_KEY = 'realmery.settings';
const SKINS_KEY = 'realmery.skins';
const LAST_REALM_KEY = 'realmery.lastRealm';

/** Checks one stored value against its setting's kind and choices; anything else is the default. */
function valid<K extends keyof Settings>(key: K, value: unknown): value is Settings[K] {
  const fallback = DEFAULTS[key];
  if (typeof value !== typeof fallback) return false;
  switch (key) {
    case 'general.onStart':
      return ON_START_CHOICES.some(([id]) => id === value);
    case 'general.startRealm':
      return typeof value === 'string' && value.length <= 200;
    case 'appearance.textSize':
      return TEXT_SIZES.some(([id]) => id === value);
    case 'appearance.writingFont':
      return WRITING_FONTS.some(([id]) => id === value);
    case 'appearance.writingSize':
      return Number.isInteger(value) && (value as number) >= 0 && (value as number) <= 48;
    case 'appearance.skin':
      return typeof value === 'string' && (value === AUTOMATIC || BUILT_IN_SKINS.some((s) => s.id === value) || /^custom:[a-z0-9-]{1,60}$/.test(value));
    case 'new.entryType':
      return (ENTRY_TYPES as readonly unknown[]).includes(value);
    case 'new.entryAudience':
      return (AUDIENCES as readonly unknown[]).includes(value);
    case 'dice.historyLimit':
      return Number.isInteger(value) && (value as number) >= 10 && (value as number) <= 5000 && (value as number) % 10 === 0;
    case 'new.diceCount':
      return Number.isInteger(value) && (value as number) >= 1 && (value as number) <= 100;
    case 'new.sceneWordGoal':
      return Number.isInteger(value) && (value as number) >= 0 && (value as number) <= 100_000;
    case 'writing.sceneRevisions':
      return Number.isInteger(value) && (value as number) >= 1 && (value as number) <= 200;
    case 'writing.focusIdleMinutes':
      return Number.isInteger(value) && (value as number) >= 1 && (value as number) <= 60;
    case 'print.paperSize':
      return value === 'A4' || value === 'LETTER';
    case 'atlas.units':
      return UNIT_SYSTEMS.some(([id]) => id === value);
    case 'new.mapGrid':
      return MAP_GRIDS.some(([id]) => id === value);
    case 'new.mapUnit':
      return typeof value === 'string' && value.length <= 40;
    case 'writing.spelling':
      return typeof value === 'boolean';
    case 'writing.dialect':
      return value === 'US' || value === 'UK';
    default:
      return true;
  }
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function load(): Settings {
  let stored: Record<string, unknown> = {};
  try {
    const parsed: unknown = JSON.parse(read(SETTINGS_KEY) ?? '{}');
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) stored = parsed as Record<string, unknown>;
  } catch {
    // Unreadable: the defaults.
  }
  const settings: Record<string, unknown> = { ...DEFAULTS };
  for (const key of Object.keys(DEFAULTS) as (keyof Settings)[]) {
    if (valid(key, stored[key])) settings[key] = stored[key];
  }
  return settings as unknown as Settings;
}

let current: Settings = load();
const listeners = new Set<() => void>();

export function settings(): Settings {
  return current;
}

/** Changes settings; returns false when this device couldn't keep them (they still apply until the page closes). */
export function saveSettings(changes: Partial<Settings>): boolean {
  const next = { ...current } as Record<string, unknown>;
  for (const [key, value] of Object.entries(changes) as [keyof Settings, unknown][]) {
    if (valid(key, value)) next[key] = value;
  }
  current = next as unknown as Settings;
  const kept = write(SETTINGS_KEY, JSON.stringify(current));
  applyAppearance();
  listeners.forEach((listener) => listener());
  return kept;
}

export function useSettings(): Settings {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => current,
  );
}

// ---- skins of the user's own ----

export function customSkins(): Skin[] {
  try {
    const parsed: unknown = JSON.parse(read(SKINS_KEY) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((item: unknown) => {
      const { id, css } = (item ?? {}) as { id?: unknown; css?: unknown };
      return typeof id === 'string' && /^custom:[a-z0-9-]{1,60}$/.test(id) && typeof css === 'string' && css.length <= 20_000 ? [parseSkin(css, id)] : [];
    });
  } catch {
    return [];
  }
}

/** Keeps a desktop skin file on this device, and returns it; throws when it has no colours this app can use. */
export function importSkin(css: string): Skin {
  if (css.length > 20_000) throw new Error('That file is too large to be a skin.');
  const probe = parseSkin(css, 'custom:probe');
  if (Object.keys(probe.colors).length === 0) throw new Error('That file has no Realmery skin colours in it.');
  const base = probe.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-+)|(-+$)/g, '').slice(0, 50) || 'skin';
  const stored = (() => {
    try {
      const parsed: unknown = JSON.parse(read(SKINS_KEY) ?? '[]');
      return Array.isArray(parsed) ? (parsed as { id: string; css: string }[]) : [];
    } catch {
      return [];
    }
  })();
  let id = `custom:${base}`;
  for (let n = 2; stored.some((s) => s.id === id); n++) id = `custom:${base}-${n}`;
  write(SKINS_KEY, JSON.stringify([...stored, { id, css }]));
  return parseSkin(css, id);
}

export function removeCustomSkin(id: string): void {
  try {
    const parsed: unknown = JSON.parse(read(SKINS_KEY) ?? '[]');
    if (Array.isArray(parsed)) write(SKINS_KEY, JSON.stringify(parsed.filter((s: { id?: unknown }) => s?.id !== id)));
  } catch {
    // Nothing kept to remove.
  }
  if (current['appearance.skin'] === id) saveSettings({ 'appearance.skin': AUTOMATIC });
}

export function allSkins(): Skin[] {
  return [...BUILT_IN_SKINS, ...customSkins()];
}

// ---- the last realm opened, for "Reopen the last realm" ----

export function rememberLastRealm(realmKey: string): void {
  write(LAST_REALM_KEY, realmKey);
}

export function lastRealm(): string | null {
  return read(LAST_REALM_KEY);
}

// ---- applying appearance ----

/** Puts the skin, text size, writing font and animations on the page. */
export function applyAppearance(root: HTMLElement = document.documentElement): void {
  const s = current;
  const skin = s['appearance.skin'] === AUTOMATIC ? null : (allSkins().find((k) => k.id === s['appearance.skin']) ?? null);
  applySkin(skin, root);
  root.style.setProperty('--text-scale', s['appearance.textSize']);
  if (s['appearance.writingFont']) root.style.setProperty('--font-writing', s['appearance.writingFont']);
  else root.style.removeProperty('--font-writing');
  if (s['appearance.writingSize'] > 0) root.style.setProperty('--size-writing', `${s['appearance.writingSize']}pt`);
  else root.style.removeProperty('--size-writing');
  if (s['appearance.animations']) delete root.dataset['animations'];
  else root.dataset['animations'] = 'off';
}

let started = false;

/**
 * True only the first time it's asked in a page load: the realms page then honours
 * "When Realmery starts". Opening a realm first (a link, a reload) counts as the start.
 */
export function claimStart(): boolean {
  const first = !started;
  started = true;
  return first;
}

/** For tests: reread storage as a fresh page would. */
export function reloadSettings(): void {
  current = load();
}
